import Tooltip from "@kea-mod/common/tooltip";
import { emptyAssessment } from "@kea-mod/common/types/Assessment";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle } from "react-icons/fa";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import AssessmentDataService from "../../axios/services/assessment.service";
import FeedbackDataService from "../../axios/services/feedback.service";
import SubmissionExerciseDataService from "../../axios/services/submission-exercises.service";
import { CommentField } from "./AssessmentFields/CommentField";
import { describePickedElement, FeedbackCreation, PickedElement } from "./AssessmentFields/FeedbackCreation";
import { GradingBreakdown } from "./AssessmentFields/GradingBreakdown";
import { FeedbackEdit } from "./AssessmentFields/FeedbackEdit";
import { ScoreCalculationHelper } from "./AssessmentFields/ScoreCalculationHelper";
import { TotalscoreField } from "./AssessmentFields/TotalscoreField";
import { valuationHint, valuationLabel } from "./AssessmentFields/ValuationField";
import { getStudentSolutionModelId } from "../../shared/studentSolutionLookup";
import ExerciseDataService from "@kea-mod/common/axios/services/ExerciseDataService";
import { ModelCardComponentMC } from "@kea-mod/common/components/ModelCardComponentMC";
import { b64DecodeUnicode } from "@kea-mod/common/components/b64";
import MDEditor from "@uiw/react-md-editor";
import { isReviewed, setReviewed } from "../../shared/reviewStatusStore";
import { displayNameOfUser, setRevisionExercise, setRevisionUser } from "../../shared/revisionUserStore";
import { invalidateAssessment } from "../../shared/assessmentCache";
import ConfirmButton from "../../shared/components/ConfirmButton";

export const ManualAssessment = (): any => {
  const { t } = useTranslation();
  let { subexid, exerciseId } = useParams();

  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(emptyAssessment);
  const [feedbacklist, setFeedbacklist] = useState<any>([]);
  const [manualFeedback, setManualFeedback] = useState<any>([]);
  const [realFeedback, setRealFeedback] = useState<any>([]);
  const [ManFeedToRemove, setManFeedToRemove] = useState([]);
  const [getback, setGetBack] = useState(false);
  const [multi, setMulti] = useState(false);
  const [modalState, setModalstate] = useState(false);
  const [revised, setRevised] = useState(false);
  const [modalStateEdit, setModalstateEdit] = useState(false);
  const [id, setId] = useState(-1);
  const [feedtype, setFeedtype] = useState({
    id: -1,
    content: "",
    category: "",
    valuation: "NEGATIVE",
  });
  const [refresh, setRefresh] = useState(false);
  const [modelId, setModelId] = useState<any>(-1);
  const [modelLoadFailed, setModelLoadFailed] = useState(false);
  // Correcting a model means comparing it against the task it was written for, so the
  // wording is kept next to the model instead of only in the exercise view.
  const [taskDescription, setTaskDescription] = useState("");
  const [taskScenario, setTaskScenario] = useState("");
  const [taskOpen, setTaskOpen] = useState(false);
  // The maximum score is configured at the exercise; the revision only shows it.
  const [exerciseMaxScore, setExerciseMaxScore] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<"grader" | "category" | "none">("grader");
  const [collapsedGroups, setCollapsedGroups] = useState<any>({});
  const [sortField, setSortField] = useState<string>("number");
  const [sortAsc, setSortAsc] = useState(true);
  // Saving and finishing a correction are two different statements, so the mark is
  // never set by saving alone. The box starts empty every time and can only add the
  // mark, taking it back happens in the submission list.
  const [markReviewed, setMarkReviewed] = useState(false);
  const [pickedElement, setPickedElement] = useState<PickedElement | null>(null);
  const [shownFeedbackIds, setShownFeedbackIds] = useState<Array<any>>([]);
  const [pendingScroll, setPendingScroll] = useState<{ id: string; block: ScrollLogicalPosition } | null>(null);
  const openFeedbackRef = useRef<(() => void) | null>(null);
  const tellEditor = (message: { type: string; id?: string; numbers?: Record<string, Array<number>> }) => {
    const frame = document.querySelector("iframe") as HTMLIFrameElement | null;
    frame?.contentWindow?.postMessage(message, "*");
  };
  const clearPickedElement = () => {
    setPickedElement(null);
    tellEditor({ type: "clearElementPick" });
  };
  const alreadyReviewed = isReviewed(exerciseId, subexid);

  useEffect(() => {
    if (getback) {
      navigate(-1); // go to previous page
    }
  }, [getback, navigate, exerciseId]);

  // The embedded editor reports which element was clicked, so a manual entry can be
  // anchored to it the same way grader messages are.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "openFeedbackForPick") {
        openFeedbackRef.current?.();
        return;
      }
      if (event.data?.type !== "elementPicked") return;
      setPickedElement(
        event.data.id
          ? {
              id: event.data.id,
              label: event.data.label,
              elementType: event.data.elementType,
              eventType: event.data.eventType,
            }
          : null,
      );
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Scrolling waits for the row to exist: opening a group and marking the entry are two
  // state changes, and the row is only in the page once React has drawn them.
  useEffect(() => {
    if (!pendingScroll) return;
    const row = document.getElementById(pendingScroll.id);
    if (row) row.scrollIntoView({ behavior: "smooth", block: pendingScroll.block });
    else document.getElementById("feedback-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setPendingScroll(null);
  }, [pendingScroll]);

  // The editor draws the running number at every anchored element, so a message and its
  // mark can be matched without clicking. It gets the numbers from here, and it asks again
  // whenever it has reloaded its own copy of the feedback.
  useEffect(() => {
    const sendNumbers = () => tellEditor({ type: "showNumbers", numbers: getNumbersByElement() });
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "numbersPlease") sendNumbers();
      // The way back: the mark in the model names its element, the list scrolls to the
      // messages that hang on it and marks all of them, not just the first.
      if (event.data?.type === "showFeedbackFor") {
        const elementId = String(event.data.id ?? "");
        const matches = feedbacklist.filter((feedback: any) => feedback.positionId === elementId);
        if (matches.length === 0) return;
        setShownFeedbackIds(matches.map((feedback: any) => feedback.id));
        // A collapsed group holds no row to scroll to, so the group is opened first.
        setCollapsedGroups((groups: any) => ({ ...groups, [groupNameOf(matches[0])]: false }));
        setPendingScroll({ id: rowElementId(matches[0]), block: "center" });
      }
    };
    window.addEventListener("message", onMessage);
    sendNumbers();
    return () => window.removeEventListener("message", onMessage);
    // groupBy belongs here: the handler decides from it which group holds the row it has
    // to open, and would otherwise still see the grouping that was set when it was bound.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbacklist, groupBy]);

  useEffect(() => {
    SubmissionExerciseDataService.get(subexid)
      .then((response: any) => {
        //console.log(response.data.data);
        let as = response.data.data;
        if (as.comment === null) {
          as["comment"] = "";
        }
        console.log(as);
        setId(as.id);
        setAssessment(as);
        setRevised(as.isRevised);
        //console.log(as);
        setInitialFeedbacklist(as);
      })
      .catch((e: any) => {
        console.log(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subexid, refresh]);

  // Exercise and assessment are fetched on their own and either can finish first, so both
  // are dependencies here. A later change at the exercise applies to every revision.
  useEffect(() => {
    if (exerciseMaxScore === null) return;
    setAssessment((current: any) => {
      if (current.maxTotalScore === exerciseMaxScore) return current;
      return { ...current, maxTotalScore: exerciseMaxScore };
    });
  }, [exerciseMaxScore, id, assessment.maxTotalScore]);

  // Load the model of this submission so it can be shown next to the assessment form.
  useEffect(() => {
    if (!subexid) return;
    setModelLoadFailed(false);
    SubmissionExerciseDataService.getSubmissionExercise(subexid)
      .then((response: any) => {
        const submissionExercise = response.data.data;
        // Opening this page directly, or reloading it, has to name the right person, so
        // the breadcrumb takes its name from the submission that was actually loaded.
        setRevisionUser(subexid, displayNameOfUser(submissionExercise.user));
        // Same for the exercise, and from the same response, so both parts of the trail describe
        // the submission that was actually loaded.
        setRevisionExercise(subexid, submissionExercise.exercise?.name ?? "");
        const exId = submissionExercise.exerciseId;
        // Both texts are stored base64-encoded, the scenario only exists for creation exercises.
        setTaskDescription(b64DecodeUnicode(submissionExercise.exercise?.description ?? "") ?? "");
        setTaskScenario(b64DecodeUnicode(submissionExercise.exercise?.creation?.scenario ?? "") ?? "");
        const exerciseMax = submissionExercise.exercise?.maxTotalScore;
        if (exerciseMax !== undefined && exerciseMax !== null) {
          setExerciseMaxScore(String(exerciseMax));
        }
        if (submissionExercise.type === "creation") {
          getStudentSolutionModelId(exId, subexid)
            .then((foundModelId: any) => {
              if (foundModelId === undefined) {
                setModelLoadFailed(true);
                return;
              }
              setModelId(foundModelId);
            })
            .catch((e: any) => {
              console.log(e);
              setModelLoadFailed(true);
            });
        } else {
          ExerciseDataService.get(exId, "authorui")
            .then((res: any) => {
              const inputModelId = res.data.data.understanding.models.inputs[0]?.id;
              if (inputModelId === undefined) {
                setModelLoadFailed(true);
                return;
              }
              setModelId(inputModelId);
            })
            .catch((e: any) => {
              console.log(e);
              setModelLoadFailed(true);
            });
        }
      })
      .catch((e: any) => {
        console.log(e);
        setModelLoadFailed(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subexid]);

  function refreshManFeedback() {
    if (manualFeedback.length > 0) {
      let manFee = manualFeedback[manualFeedback.length - 1];
      feedbacklist.push(manFee);
    }
  }

  function setInitialFeedbacklist(assessment: any) {
    var Feedbacks = [];
    var manFeed = [];
    var realfeed = [];
    for (var o = 0; o < assessment.feedbacksmanual.length; o++) {
      let manual = assessment.feedbacksmanual[o];
      manual["grader"] = "Manual Feedback";
      Feedbacks.push(manual);
      manFeed.push(manual);
    }

    for (var i = 0; i < assessment.gradings.length; i++) {
      let grading = assessment.gradings[i];

      for (var a = 0; a < grading.feedbacks.length; a++) {
        let feedback = grading.feedbacks[a];
        if (grading.gradingservice.name === "Multiple Choice") {
          setMulti(true);
        } else {
          if (!(feedback.category === "internal" && feedback.valuation === "OTHER")) {
            feedback["grader"] = grading.gradingservice.name;
            Feedbacks.push(feedback);
            realfeed.push(feedback);
          }
        }
      }
    }
    Feedbacks.sort(sortByGrader);
    setFeedbacklist(Feedbacks);
    setManualFeedback(manFeed);
    setRealFeedback(realfeed);
  }
  function back() {
    deleteAfterBack();
  }
  const saveAssessment = () => {
    let com = assessment.comment;
    if (assessment.comment === null || assessment.comment === "") {
      com = " ";
    }
    let data = {
      comment: com,
      totalscore: assessment.totalscore,
      maxTotalScore: assessment.maxTotalScore,
      isRevised: revised,
    };

    console.log(data);
    AssessmentDataService.update(id, data)

      .then((_response: any) => {
        if (markReviewed) {
          setReviewed(exerciseId, subexid, true);
        }
        // The saved revision changes the assessment behind the cached list entry.
        invalidateAssessment(exerciseId, subexid);
        setGetBack(true);
      })
      .catch((e: any) => {
        console.log(e);
      });
  };

  const toggleModal = () => {
    if (modalState) {
      setRefresh(!refresh);
    }
    setModalstate(!modalState);
  };

  const toggleModalEdit = () => {
    if (modalStateEdit) {
      setRefresh(!refresh);
    }
    setModalstateEdit(!modalStateEdit);
  };

  const handleChangeComment = (event: any) => {
    let value = event.target.value;

    setAssessment({
      ...assessment,
      comment: value,
    });
  };
  // The score fields hand over absolute points. Multiple choice results are stored that
  // way, every other assessment stores the score as a factor of the maximum, so the
  // points are converted back here before they are kept.
  function onChangeTotalScore(val: string) {
    let stored = val;

    if (!multi) {
      const points = parseFloat(val);
      const max = parseFloat(assessment.maxTotalScore);
      if (isNaN(points) || isNaN(max) || max <= 0) return;
      stored = (points / max).toString();
    }

    setAssessment({
      ...assessment,
      totalscore: stored,
    });
  }

  function saveData() {
    //update data before toggling modal
    let com = assessment.comment;
    if (assessment.comment === null || assessment.comment === "") {
      com = " ";
    }

    let data = {
      comment: com,
      totalscore: assessment.totalscore,
      maxTotalScore: assessment.maxTotalScore,
      isRevised: revised,
    };

    AssessmentDataService.update(id, data).catch((e: any) => {
      console.log(e);
    });
  }

  function createFeedback() {
    saveData();
    toggleModal();
  }
  openFeedbackRef.current = createFeedback;

  const deleteAfterBack = async () => {
    for (var i = 0; i < ManFeedToRemove.length; i++) {
      let feedObj = ManFeedToRemove[i] as any;

      await FeedbackDataService.deleteManual(feedObj.id)
        .then(() => {})
        .catch((e: any) => {
          console.log(e);
        });
      if (i === ManFeedToRemove.length - 1) {
        setGetBack(true);
      }
    }
    if (ManFeedToRemove.length === 0) {
      setGetBack(true);
    }
  };

  function sortByGrader(a: any, b: any) {
    if (a.grader < b.grader) {
      return -1;
    }
    if (a.grader > b.grader) {
      return 1;
    }
    return 0;
  }
  function editFeedback(feedback: any) {
    setFeedtype(feedback);
    saveData();
    toggleModalEdit();
  }
  function goToExercisePage() {
    navigate("/exercises/" + exerciseId);
  }
  function switcher() {
    setRevised(!revised);
  }

  // Deleting takes effect right away, like creating does. Leaving it until the correction is
  // saved meant the entry came back on the next load, which read as if nothing had happened.
  async function deleteFeedback(id: any) {
    const feedback = feedbacklist.find((entry: any) => entry.id === id);
    if (!feedback) return;

    // Manual entries and grader messages live in different tables, as the previous
    // delete-on-save round already distinguished.
    let remove;
    if (containsObject(feedback, manualFeedback)) remove = FeedbackDataService.deleteManual(id);
    else if (containsObject(feedback, realFeedback)) remove = FeedbackDataService.delete(id);
    else return;

    try {
      await remove;
      setFeedbacklist(feedbacklist.filter((entry: any) => entry.id !== id));
      setManFeedToRemove(ManFeedToRemove.filter((entry: any) => (entry as any).id !== id));
      tellEditor({ type: "feedbackChanged" });
    } catch (e: any) {
      console.log(e);
    }
  }

  function containsObject(obj: any, list: any) {
    var i;
    for (i = 0; i < list.length; i++) {
      if (list[i] === obj) {
        return true;
      }
    }

    return false;
  }
  function getEmptyMessageSubmission() {
    return <p className="is-size-7">{t("noFeedbacks")} </p>;
  }

  // Distinct colour per valuation level, with the label kept as a colour-independent
  // second cue so the levels are told apart without relying on colour alone.
  function getValuationClass(valuation: string) {
    switch (valuation) {
      case "POSITIVE":
        return "is-success";
      case "NEGATIVE":
        return "is-danger";
      case "NEUTRAL":
        return "is-info";
      case "OTHER":
        return "is-warning";
      default:
        return "is-light";
    }
  }

  // Stable running number per feedback (independent of sorting and grouping)
  // so messages can be referenced unambiguously, e.g. in comments.
  const getFeedbackNumbers = () => {
    const numbers: any = {};
    [...feedbacklist]
      .sort((a: any, b: any) => a.id - b.id)
      .forEach((feedback: any, index: number) => {
        numbers[(feedback.grader || "") + "#" + feedback.id] = index + 1;
      });
    return numbers;
  };

  const groupNameOf = (feedback: any) => (groupBy === "grader" ? feedback.grader : feedback.category) || "?";

  const rowElementId = (feedback: any) =>
    "feedback-row-" + String(feedback.id) + "-" + String(feedback.grader || "").replace(/[^A-Za-z0-9_-]/g, "");

  // Which numbers sit at which model element. Only anchored entries appear here, and an
  // element can carry several of them.
  const getNumbersByElement = () => {
    const numbers = getFeedbackNumbers();
    const byElement: Record<string, Array<number>> = {};
    feedbacklist.forEach((feedback: any) => {
      const elementId = feedback.positionId;
      if (typeof elementId !== "string" || elementId === "") return;
      const number = numbers[(feedback.grader || "") + "#" + feedback.id];
      if (typeof number !== "number") return;
      byElement[elementId] = [...(byElement[elementId] || []), number];
    });
    Object.keys(byElement).forEach((elementId) => byElement[elementId].sort((a, b) => a - b));
    return byElement;
  };

  function getFeedbackTableItem(feedback: any, feedbackNumbers: any) {
    // A message that names a model element can point at it, so the list and the model
    // are no longer two separate views of the same correction.
    const anchored = typeof feedback.positionId === "string" && feedback.positionId !== "";
    const showInModel = () => {
      if (!anchored) return;
      setShownFeedbackIds([feedback.id]);
      tellEditor({ type: "showElement", id: feedback.positionId });
    };
    return (
      <tr
        key={"Feedback" + feedback.grader + feedback.id}
        id={rowElementId(feedback)}
        className={shownFeedbackIds.includes(feedback.id) ? "has-background-warning-light" : undefined}
      >
        <th>
          <span
            className="tag"
            style={
              anchored
                ? { backgroundColor: "#ff9f1c", color: "#ffffff", fontWeight: "bold" }
                : { backgroundColor: "transparent", color: "#7a7a7a" }
            }
            title={(anchored ? t("numberAtElement") : t("numberWithoutElement")) as string}
          >
            {feedbackNumbers[(feedback.grader || "") + "#" + feedback.id]}
          </span>
        </th>
        <th
          onClick={showInModel}
          style={anchored ? { cursor: "pointer" } : undefined}
          title={anchored ? (t("showInModel") as string) : undefined}
        >
          {feedback.content}
        </th>
        <th>{feedback.category}</th>
        <th>
          <span
            className={"tag " + getValuationClass(feedback.valuation)}
            title={valuationHint(feedback.valuation, t) || undefined}
          >
            {valuationLabel(feedback.valuation, t)}
          </span>
        </th>
        <th>{feedback.grader}</th>
        <td>
          <div className="is-flex" style={{ gap: "0.5rem" }}>
            <button
              className="button is-primary is-small"
              disabled={feedback.grader === "Multiple Choice"}
              onClick={() => editFeedback(feedback)}
            >
              {t("edit")}
            </button>
            <ConfirmButton
              id={feedback.id}
              name={feedback.content}
              proceedAction={deleteFeedback}
              question={`deleteFeedbackQuestion`}
              explanation={`deleteFeedbackExplanation`}
              color="button is-danger is-small"
              buttonLabel={`delete`}
            />
          </div>
        </td>
      </tr>
    );
  }

  const toggleGroup = (name: string) => {
    const opening = Boolean(collapsedGroups[name]);
    setCollapsedGroups({ ...collapsedGroups, [name]: !collapsedGroups[name] });
    if (!opening) return;
    // The box the table sits in has its own scrollbar, so a group opened at its lower edge
    // grows out of sight and looks as if the click had done nothing.
    const first = getSortedFeedbacks(feedbacklist.filter((feedback: any) => groupNameOf(feedback) === name))[0];
    if (first) setPendingScroll({ id: rowElementId(first), block: "nearest" });
  };

  const onSortColumn = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortIndicator = (field: string) => {
    if (sortField !== field) return "";
    return sortAsc ? " ▲" : " ▼";
  };

  function getSortedFeedbacks(list: any[]) {
    if (sortField === "") return list;
    const numbers = getFeedbackNumbers();
    const sortValue = (item: any) =>
      sortField === "number"
        ? numbers[(item.grader || "") + "#" + item.id]
        : (item[sortField] || "").toString().toLowerCase();
    return [...list].sort((a: any, b: any) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });
  }

  function getGroupedFeedbacks() {
    if (groupBy === "none") {
      return [{ name: "", items: getSortedFeedbacks(feedbacklist) }];
    }
    const groups: any = {};
    feedbacklist.forEach((feedback: any) => {
      const key = groupNameOf(feedback);
      if (!groups[key]) groups[key] = [];
      groups[key].push(feedback);
    });
    return Object.keys(groups)
      .sort()
      .map((name) => ({ name, items: getSortedFeedbacks(groups[name]) }));
  }

  function getGroupHeaderRow(name: string, count: number) {
    return (
      <tr key={"group" + name}>
        <td colSpan={6} style={{ borderBottom: "2px solid #dbdbdb" }}>
          <button className="button is-small is-white px-1" onClick={() => toggleGroup(name)}>
            <span className="mr-1">{collapsedGroups[name] ? "▸" : "▾"}</span>
            <strong>{name}</strong>
            <span className="ml-1 has-text-grey">({count})</span>
          </button>
        </td>
      </tr>
    );
  }

  const headCellStyle = { position: "sticky", top: 0, background: "white", zIndex: 1 } as const;
  const sortableHeadCellStyle = { ...headCellStyle, cursor: "pointer" };

  function getGroupingControl() {
    return (
      <div className="is-flex is-align-items-center">
        <span className="mr-2 is-size-7 has-text-grey">{t("groupBy")}</span>
        <div className="buttons has-addons mb-0">
          <button
            className={"button is-small mb-0" + (groupBy === "grader" ? " is-info is-selected" : "")}
            onClick={() => setGroupBy("grader")}
          >
            {t("gradingService")}
          </button>
          <button
            className={"button is-small mb-0" + (groupBy === "category" ? " is-info is-selected" : "")}
            onClick={() => setGroupBy("category")}
          >
            {t("category")}
          </button>
          <button
            className={"button is-small mb-0" + (groupBy === "none" ? " is-info is-selected" : "")}
            onClick={() => setGroupBy("none")}
          >
            {t("noGrouping")}
          </button>
        </div>
      </div>
    );
  }

  function getfeedbackTable() {
    const groups = getGroupedFeedbacks();
    const feedbackNumbers = getFeedbackNumbers();
    return (
      <div className="table-container" style={{ maxHeight: "clamp(300px, 42vh, 480px)", overflowY: "auto" }}>
        <table className="table is-striped is-narrow is-hoverable is-fullwidth">
          <thead className="is-italic">
            <tr>
              <td onClick={() => onSortColumn("number")} style={sortableHeadCellStyle}>
                #{sortIndicator("number")}
              </td>
              <td onClick={() => onSortColumn("content")} style={sortableHeadCellStyle}>
                {t("content")}
                {sortIndicator("content")}
              </td>
              <td onClick={() => onSortColumn("category")} style={sortableHeadCellStyle}>
                {t("category")}
                {sortIndicator("category")}
              </td>
              <td onClick={() => onSortColumn("valuation")} style={sortableHeadCellStyle}>
                {t("valuation")}
                {sortIndicator("valuation")}
              </td>
              <td onClick={() => onSortColumn("grader")} style={sortableHeadCellStyle}>
                {t("gradingService")}
                {sortIndicator("grader")}
              </td>
              <td style={headCellStyle}>{t("options")}</td>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const rows = [];
              if (groupBy !== "none") {
                rows.push(getGroupHeaderRow(group.name, group.items.length));
              }
              if (groupBy === "none" || !collapsedGroups[group.name]) {
                group.items.forEach((feedback: any) => {
                  rows.push(getFeedbackTableItem(feedback, feedbackNumbers));
                });
              }
              return rows;
            })}
          </tbody>
        </table>
      </div>
    );
  }
  const Modal: any = ({ children, closeModal, modalState, title }: any) => {
    if (!modalState) {
      return null;
    }

    return (
      <div className="modal is-active compact-modal">
        <div
          className="modal-background"
          onClick={function (_event: any) {
            closeModal();
          }}
        />
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">{title}</p>
            <button
              className="delete"
              onClick={function (_event: any) {
                closeModal();
              }}
            />
          </header>
          <section className="modal-card-body">
            <div className="content">{children}</div>
          </section>
          <footer className="modal-card-foot">
            <button
              className="button is-danger"
              onClick={function (_event: any) {
                closeModal();
              }}
            >
              {t("cancel")}
            </button>
          </footer>
        </div>
      </div>
    );
  };

  Modal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    modalState: PropTypes.bool.isRequired,
    title: PropTypes.string,
  };

  const ModalEdit: any = ({ children, closeModal, modalState, title }: any) => {
    if (!modalState) {
      return null;
    }

    return (
      <div className="modal is-active compact-modal">
        <div
          className="modal-background"
          onClick={function (_event: any) {
            closeModal();
          }}
        />
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">{title}</p>
            <button
              className="delete"
              onClick={function (_event: any) {
                closeModal();
              }}
            />
          </header>
          <section className="modal-card-body">
            <div className="content">{children}</div>
          </section>
          <footer className="modal-card-foot">
            <button
              className="button"
              onClick={function (_event: any) {
                closeModal();
              }}
            >
              {t("cancel")}
            </button>
          </footer>
        </div>
      </div>
    );
  };

  ModalEdit.propTypes = {
    closeModal: PropTypes.func.isRequired,
    modalState: PropTypes.bool.isRequired,
    title: PropTypes.string,
  };

  let renderFeedbackTable = (
    <div>
      <div>
        <div className="columns">
          <div className="column has-text-left">
            {feedbacklist.length > 0 && getfeedbackTable()}
            {feedbacklist.length === 0 && getEmptyMessageSubmission()}
          </div>
        </div>
      </div>
    </div>
  );

  // The panel is reserved from the first render on, not only once the lookup has returned.
  // Waiting for it made the page start in a single column and jump into two a few seconds
  // later, which reads as the model being absent rather than still on its way.
  const modelPanelVisible = !modelLoadFailed;
  const modelReady = modelId !== -1 && modelId !== undefined;

  return (
    <div className={modelPanelVisible ? "container is-fluid mt-6" : "container mt-6"}>
      <div className="section">
        {modelLoadFailed && <div className="notification is-danger is-light">{t("modelLoadFailed")}</div>}
        <div className="box">
          {modelPanelVisible && (taskDescription !== "" || taskScenario !== "") && (
            <div style={{ borderBottom: "1px solid #ededed", paddingBottom: "0.5rem", marginBottom: "0.75rem" }}>
              {/* Collapsed by default so the panels below keep their height; the wording is only
                  needed when a correction is actually in doubt. */}
              <button
                type="button"
                className="button is-ghost has-text-weight-semibold p-0"
                style={{ textDecoration: "none" }}
                onClick={() => setTaskOpen(!taskOpen)}
                aria-expanded={taskOpen}
              >
                {taskOpen ? "▾" : "▸"}&nbsp;{t("exercise")}
              </button>
              {taskOpen && (
                <div className="mt-3" data-color-mode="light">
                  {taskDescription !== "" && (
                    <>
                      <h5 className="title is-6 mb-1">{t("description")}</h5>
                      <div className="content">
                        <MDEditor.Markdown source={taskDescription} />
                      </div>
                    </>
                  )}
                  {taskScenario !== "" && (
                    <>
                      <h5 className="title is-6 mb-1 mt-3">{t("scenario")}</h5>
                      <div className="content">
                        <MDEditor.Markdown source={taskScenario} />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="columns is-variable is-5">
            {modelPanelVisible && (
              <div className="column is-9">
                <h4 className="title is-5" style={{ marginBottom: "0.75rem" }}>
                  {t("model")}
                </h4>
                <div
                  style={{
                    border: "1px solid #dbdbdb",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  {/* Same embedding as the results page: the editor loads the assessments of this
                      submission itself and marks the element each message is anchored to. */}
                  {/* Trimmed from `100vh - 14rem` so the feedback block reaches the bottom. */}
                  {modelReady ? (
                    <ModelCardComponentMC
                      modelId={modelId.toString()}
                      submissionExerciseId={subexid as string}
                      height="clamp(380px, calc(100vh - 20rem), 640px)"
                      zoomEnabled
                      authorView
                    />
                  ) : (
                    // Same height as the editor, so nothing moves once it arrives.
                    <div
                      style={{
                        height: "clamp(380px, calc(100vh - 20rem), 640px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.75rem",
                        background: "#fafafa",
                      }}
                    >
                      <span
                        className="button is-loading"
                        style={{
                          border: "none",
                          background: "transparent",
                          boxShadow: "none",
                          width: "2rem",
                          height: "2rem",
                        }}
                      />
                      <span className="has-text-grey">{t("modelLoading")}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div
              className={modelPanelVisible ? "column is-3" : "column"}
              style={
                modelPanelVisible
                  ? { borderLeft: "1px solid #ededed", display: "flex", flexDirection: "column" }
                  : undefined
              }
            >
              <h4 className="title is-5" style={{ marginBottom: "0.75rem" }}>
                {t("ManualAssessment")}
              </h4>
              <CommentField activeAssessment={assessment} h={handleChangeComment} />
              <TotalscoreField activeAssessment={assessment} h={onChangeTotalScore} multiple={multi} />
              <ScoreCalculationHelper
                maxTotalScore={assessment.maxTotalScore}
                onApply={onChangeTotalScore}
                feedbackOptions={feedbacklist}
              />
              <GradingBreakdown gradings={assessment.gradings} maxTotalScore={assessment.maxTotalScore} />
              <div className="field">
                <label className="mt-5 label">
                  {t("isRevised")}
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                    content={t("isrevisedTooltip")}
                  ></Tooltip>
                </label>
                <input name="autoAssessment" type="checkbox" checked={revised} onChange={() => switcher()} />
              </div>

              <div className="field">
                <label className="mt-5 label">
                  {t("markAsReviewed")}
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                    content={t("markAsReviewedTooltip")}
                  ></Tooltip>
                </label>
                {alreadyReviewed ? (
                  <p className="is-size-7">
                    <span className="tag is-success is-light">{t("reviewedDone")}</span> {t("markAsReviewedAlready")}
                  </p>
                ) : (
                  <input
                    name="markAsReviewed"
                    type="checkbox"
                    checked={markReviewed}
                    onChange={() => setMarkReviewed(!markReviewed)}
                  />
                )}
              </div>

              {feedbacklist.length > 0 && (
                <button
                  type="button"
                  className="button is-ghost is-size-7 p-0 mt-5"
                  style={{
                    textDecoration: "none",
                    height: "auto",
                    // Without this the button stretches across the flex column and reads as a field.
                    alignSelf: "flex-start",
                    border: "none",
                    background: "transparent",
                  }}
                  onClick={() =>
                    document.getElementById("feedback-section")?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                >
                  {t("feedbackEntryCount", { count: feedbacklist.length })}&nbsp;↓
                </button>
              )}

              <div
                style={{
                  marginTop: "auto",
                  paddingTop: "1.25rem",
                  borderTop: "1px solid #ededed",
                  display: "flex",
                  gap: "0.75rem",
                }}
              >
                <button className="button is-danger" onClick={() => back()}>
                  {t("back")}
                </button>

                <button className="button is-success" onClick={() => saveAssessment()}>
                  {t("save")}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="box" id="feedback-section">
          <h4 className="title is-5" style={{ marginBottom: "0.75rem" }}>
            {t("Feedback")}
            <Tooltip
              icon="circle-info"
              class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
              content={t("feedbackTooltip")}
            ></Tooltip>
          </h4>
          <div className="is-flex is-align-items-center mb-4" style={{ gap: "1.5rem", flexWrap: "wrap" }}>
            <button className="button is-info" onClick={() => createFeedback()}>
              {t("createFeedback")}
            </button>
            {pickedElement ? (
              <span className="is-flex is-align-items-center" style={{ gap: "0.5rem" }}>
                <span className="tag is-info is-light">
                  {t("feedbackAnchor")}: {describePickedElement(pickedElement, t)}
                </span>
                <button className="button is-small is-text" onClick={clearPickedElement}>
                  {t("feedbackAnchorClear")}
                </button>
              </span>
            ) : (
              <span className="has-text-grey is-size-7">{t("feedbackAnchorHint")}</span>
            )}
            {feedbacklist.length > 0 && getGroupingControl()}
          </div>
          <Modal closeModal={toggleModal} modalState={modalState} title={t("createFeedback")}>
            <FeedbackCreation
              pickedElement={pickedElement}
              clearPickedElement={clearPickedElement}
              onFeedbackSaved={() => {
                setPickedElement(null);
                tellEditor({ type: "feedbackChanged" });
              }}
              feedbacklist={manualFeedback}
              toggleModal={toggleModal}
              setFeedbacklist={setManualFeedback}
              asid={assessment.id}
              refreshManFeedback={refreshManFeedback}
              ManFeedToRemove={ManFeedToRemove}
              setManFeedToRemove={setManFeedToRemove}
              sortByGrader={sortByGrader}
            />
          </Modal>
          <ModalEdit closeModal={toggleModalEdit} modalState={modalStateEdit} title={t("editFeedback")}>
            <FeedbackEdit toggleModal={toggleModalEdit} feedback={feedtype} />
          </ModalEdit>
          {multi ? (
            <div text-align="center" vertical-align="middle">
              <FaExclamationTriangle className="has-text-danger" />
              {t("noeditwarning1")}
              <button
                className="button is-link is-inverted"
                style={{ height: "24px" }}
                onClick={() => goToExercisePage()}
              >
                {t("noeditwarning2")}
              </button>
              {t("noeditwarning3")}
            </div>
          ) : (
            <div> </div>
          )}
          {renderFeedbackTable}
        </div>
      </div>
    </div>
  );
};

export default ManualAssessment;
